import { Link, useLocation } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { Home, Swords, Trophy, GitBranch, User } from 'lucide-react';

const quickLinks = [
  { icon: Home, label: 'হোম', href: '/', color: 'text-primary', activeColor: 'text-primary', activeBg: 'bg-primary/10' },
  { icon: Swords, label: 'ম্যাচ', href: '/matches', color: 'text-warning', activeColor: 'text-warning', activeBg: 'bg-warning/10' },
  { icon: Trophy, label: 'টুর্নামেন্ট', href: '/tournaments', color: 'text-gold', activeColor: 'text-gold', activeBg: 'bg-gold/10' },
  { icon: GitBranch, label: 'ডিভিশন', href: '/division', color: 'text-success', activeColor: 'text-success', activeBg: 'bg-success/10' },
  { icon: User, label: 'প্রোফাইল', href: '/dashboard', color: 'text-accent', activeColor: 'text-accent', activeBg: 'bg-accent/10' },
];

export function QuickActionMinibar() {
  const location = useLocation();
  const navRef = useRef<HTMLElement | null>(null);

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  useEffect(() => {
    const navEl = navRef.current;
    if (!navEl) return;

    const setHeightVar = () => {
      const height = navEl.getBoundingClientRect().height;
      if (height > 0) {
        document.documentElement.style.setProperty('--bottom-nav-height', `${Math.round(height)}px`);
      }
    };

    requestAnimationFrame(setHeightVar);

    const ro = new ResizeObserver(() => setHeightVar());
    ro.observe(navEl);

    return () => ro.disconnect();
  }, []);

  const triggerHaptic = () => {
    if ('vibrate' in navigator) {
      navigator.vibrate(10);
    }
  };

  return (
    // IMPORTANT: pointer-events-none prevents the bar from blocking clicks outside its visible area
    <nav ref={navRef} className="fixed bottom-0 left-0 right-0 z-50 md:hidden pointer-events-none">
      {/* Backdrop blur background (non-interactive) */}
      <div className="absolute inset-0 bg-background/95 backdrop-blur-xl border-t border-border/20 pointer-events-none" />

      {/* Navigation items (interactive) */}
      <div
        className="relative flex flex-row items-stretch justify-around w-full pointer-events-auto"
        style={{
          height: 'calc(64px + env(safe-area-inset-bottom, 0px))',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        {quickLinks.map((link) => {
          const active = isActive(link.href);
          const IconComponent = link.icon;

          return (
            <Link
              key={link.href}
              to={link.href}
              onClick={triggerHaptic}
              className={
                `
                flex flex-col items-center justify-center flex-1 gap-0.5 py-2
                transition-all duration-200 ease-out
                active:scale-95 active:opacity-80
                ${active ? link.activeBg : 'hover:bg-muted/30'}
              `
              }
              aria-label={link.label}
            >
              <div
                className={
                  `
                relative flex items-center justify-center w-10 h-7
                rounded-full transition-all duration-200
                ${active ? link.activeBg : ''}
              `
                }
              >
                <IconComponent
                  className={
                    `
                    h-5 w-5 transition-all duration-200
                    ${active ? `${link.activeColor} drop-shadow-sm` : 'text-muted-foreground'}
                  `
                  }
                  fill={active ? 'currentColor' : 'none'}
                  strokeWidth={active ? 1.5 : 2}
                />

                {active && (
                  <span
                    className={
                      `
                    absolute -top-0.5 left-1/2 -translate-x-1/2
                    w-1 h-1 rounded-full
                    ${link.activeColor.replace('text-', 'bg-')}
                  `
                    }
                  />
                )}
              </div>

              <span
                className={
                  `
                text-[10px] font-medium transition-all duration-200
                ${active ? `${link.activeColor} font-semibold` : 'text-muted-foreground'}
              `
                }
              >
                {link.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
